import React, { useEffect, useState } from "react";
import { useAuth } from "../AuthContext";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { BookOpen, Clock, Sparkles, GraduationCap, Users, Video, Calendar } from "lucide-react";

export default function Dashboard() {
  const { user, API } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);

  useEffect(() => {
    if (!user) return;
    if (user.role === "admin") {
      axios.get(`${API}/admin/stats`, { withCredentials: true }).then((r) => setStats(r.data));
      axios.get(`${API}/courses?status=pending`, { withCredentials: true }).then((r) => setCourses(r.data));
    } else if (user.role === "teacher") {
      axios.get(`${API}/courses?mine=true`, { withCredentials: true }).then((r) => setCourses(r.data));
    } else {
      axios.get(`${API}/my/enrollments`, { withCredentials: true }).then((r) => setEnrollments(r.data));
      axios.get(`${API}/courses`, { withCredentials: true }).then((r) => setCourses(r.data.slice(0, 4)));
    }
  }, [user, API]);

  if (!user) return null;

  if (user.role === "admin") {
    return (
      <div className="space-y-8">
        <Header title="Command Center" subtitle="Approvals, users, and platform health" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-stats">
          {stats && [
            { k: "Total Users", v: stats.users, icon: Users },
            { k: "Teachers Pending", v: stats.teachers_pending, icon: GraduationCap },
            { k: "Courses Pending", v: stats.courses_pending, icon: BookOpen },
            { k: "Webinars Pending", v: stats.webinars_pending, icon: Calendar },
          ].map((s) => (
            <div key={s.k} className="card-surface p-6" data-testid={`stat-${s.k.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="overline">{s.k}</div>
              <div className="mt-1 font-heading text-4xl font-semibold text-[#0A231C]">{s.v}</div>
            </div>
          ))}
        </div>

        <div className="card-surface p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Awaiting Review</div>
              <h2 className="font-heading text-xl font-semibold">Courses pending approval</h2>
            </div>
            <Link to="/admin/courses" className="text-sm text-[#D45D3C] hover:underline" data-testid="view-all-courses-link">View all →</Link>
          </div>
          {courses.length === 0 ? (
            <div className="text-sm text-[#5C5C5C]">No courses waiting for review.</div>
          ) : (
            <ul className="divide-y divide-[#F0EDE3]">
              {courses.map((c) => (
                <li key={c.id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-heading font-medium">{c.title}</div>
                    <div className="text-xs text-[#5C5C5C]">by {c.instructor_name} · {c.category}</div>
                  </div>
                  <button onClick={() => navigate("/admin/courses")} className="btn-outline text-sm">Review</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }

  if (user.role === "teacher") {
    const isApproved = user.teacher_status === "approved";
    return (
      <div className="space-y-8">
        <Header title={`Welcome back, ${user.name?.split(" ")[0]}`} subtitle="Your teaching studio" />
        {!isApproved && (
          <div className="card-surface p-6 border-l-4 border-[#D49E3C]" data-testid="teacher-pending-banner">
            <div className="overline">Status</div>
            <div className="font-heading text-lg font-semibold mt-1">Your teacher account is pending IT Admin approval.</div>
            <p className="text-sm text-[#5C5C5C] mt-1">You'll be able to create courses once approved.</p>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatTile k="Your courses" v={courses.length} />
          <StatTile k="Approved" v={courses.filter((c) => c.status === "approved").length} />
          <StatTile k="Pending" v={courses.filter((c) => c.status === "pending").length} />
          <StatTile k="Draft" v={courses.filter((c) => c.status === "draft").length} />
        </div>
        <div className="card-surface p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-semibold">Recent courses</h2>
            <Link to="/teacher/new-course" className="btn-primary text-sm" data-testid="create-course-btn">+ New course</Link>
          </div>
          {courses.length === 0 ? (
            <div className="text-sm text-[#5C5C5C]">No courses yet. Start with your first one.</div>
          ) : (
            <ul className="divide-y divide-[#F0EDE3]">
              {courses.slice(0, 5).map((c) => (
                <li key={c.id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-heading font-medium">{c.title}</div>
                    <div className="text-xs text-[#5C5C5C]">{c.category} · {c.duration_minutes || 0} min</div>
                  </div>
                  <StatusBadge s={c.status} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }

  // Learner
  return (
    <div className="space-y-10">
      <div className="grid md:grid-cols-12 gap-6 items-center">
        <div className="md:col-span-7">
          <div className="overline mb-3">Welcome</div>
          <h1 className="font-heading tracking-tight text-4xl sm:text-5xl font-semibold text-[#0A231C]">
            Hello, {user.name?.split(" ")[0]}. <span className="text-[#D45D3C]">Let's learn something today.</span>
          </h1>
          <p className="mt-4 text-[#5C5C5C] max-w-lg">
            Continue where you left off, or browse the catalog to discover a new subject.
          </p>
          <div className="mt-6 flex gap-3">
            <Link to="/catalog" className="btn-primary" data-testid="browse-catalog-btn">Browse catalog</Link>
            <Link to="/my-courses" className="btn-outline" data-testid="my-learning-btn">My learning</Link>
          </div>
        </div>
        <div className="md:col-span-5">
          <img
            src="https://images.unsplash.com/photo-1758872014929-174e4ccdf01f?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
            alt="Focused student"
            className="rounded-2xl w-full object-cover h-64 md:h-80"
          />
        </div>
      </div>

      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-2xl font-semibold">Continue learning</h2>
        </div>
        {enrollments.length === 0 ? (
          <div className="card-surface p-8 text-sm text-[#5C5C5C]">You haven't enrolled in a course yet. <Link to="/catalog" className="text-[#D45D3C] hover:underline">Explore the catalog</Link>.</div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {enrollments.map((e) => e.course && (
              <Link key={e.id} to={`/courses/${e.course.id}`} className="course-card card-surface" data-testid={`enrollment-${e.course.id}`}>
                <div className="h-40 overflow-hidden rounded-t-[14px]">
                  <img src={e.course.cover_image} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <div className="overline">{e.course.category}</div>
                  <div className="font-heading font-semibold mt-1">{e.course.title}</div>
                  <div className="mt-3 h-1.5 bg-[#E5E3D8] rounded-full overflow-hidden">
                    <div className="h-full bg-[#0A231C]" style={{ width: `${e.progress}%` }} />
                  </div>
                  <div className="mt-2 text-xs text-[#5C5C5C]">{e.progress}% complete</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-2xl font-semibold">Fresh in the catalog</h2>
          <Link to="/catalog" className="text-sm text-[#D45D3C]">See all →</Link>
        </div>
        <div className="grid md:grid-cols-4 gap-6">
          {courses.map((c) => <CourseTile key={c.id} c={c} />)}
        </div>
      </section>
    </div>
  );
}

function Header({ title, subtitle }) {
  return (
    <div>
      <div className="overline">{subtitle}</div>
      <h1 className="font-heading text-3xl md:text-4xl font-semibold text-[#0A231C]">{title}</h1>
    </div>
  );
}

function StatTile({ k, v }) {
  return (
    <div className="card-surface p-5">
      <div className="overline">{k}</div>
      <div className="font-heading text-3xl font-semibold text-[#0A231C] mt-1">{v}</div>
    </div>
  );
}

export function StatusBadge({ s }) {
  const cls = { draft: "badge-draft", pending: "badge-pending", approved: "badge-approved", rejected: "badge-rejected" }[s] || "badge-draft";
  return <span className={`text-xs px-2 py-1 rounded ${cls}`} data-testid={`status-${s}`}>{s.toUpperCase()}</span>;
}

export function CourseTile({ c }) {
  return (
    <Link to={`/courses/${c.id}`} className="course-card card-surface block" data-testid={`course-tile-${c.id}`}>
      <div className="h-40 overflow-hidden rounded-t-[14px]">
        <img src={c.cover_image || "https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?w=800"} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="p-5">
        <div className="flex items-center justify-between">
          <div className="overline">{c.category}</div>
          <div className="text-xs text-[#5C5C5C] flex items-center gap-1"><Clock size={12} /> {c.duration_minutes}m</div>
        </div>
        <div className="font-heading font-semibold text-[#0A231C] mt-1 leading-snug">{c.title}</div>
        <div className="mt-2 text-xs text-[#5C5C5C]">by {c.instructor_name}</div>
        <div className="mt-3 flex items-center justify-between">
          <div className="font-heading font-semibold text-[#0A231C]">{c.price > 0 ? `₹${c.price}` : "Free"}</div>
          {c.subscription_eligible && <div className="overline text-[#D45D3C]">Subscription</div>}
        </div>
      </div>
    </Link>
  );
}
